import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService, workScheduleService, workedSaturdaysService, inssTablesService, payslipService } from '../services/api';
import { Calendar, DollarSign, Save, User, Info, RefreshCw, Briefcase, Shield, Calculator, UserMinus, Gift, AlertTriangle, X, CheckCircle2, ChevronRight, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

const EmployeeSalary = () => {
  const queryClient = useQueryClient();

  // Visualização ativa: 'SALARY' ou '13TH'
  const [activeView, setActiveView] = useState('SALARY');

  const [selectedUserId, setSelectedUserId] = useState('');
  const [formData, setFormData] = useState({
    fixed_saturday_amount: '',
    overtime_rate_weekday: 0,
    overtime_rate_weekend: 0,
    overtime_manual_rate_weekday: '',
    overtime_manual_rate_weekend: '',
    salary_modality: 'MONTHLY',
    monthly_base_salary: '',
    inss_percentage: 7.5,
    has_work_card: true,
    apply_inss: true,
    apply_fgts: true,
    fgts_percentage: 8.0,
    net_salary_override: '',
    use_real_workload: false,
    deduct_lunch_break: true,
    calculated_hours_per_month: 220,
  });

  // Mês corrente para cálculo de horas extras
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-12

  // Ano selecionado para 13º Salário
  const [thirteenthYear, setThirteenthYear] = useState(currentYear);

  // Modal e formulário de Rescisão Contratual
  const [showTerminationModal, setShowTerminationModal] = useState(false);
  const [terminationForm, setTerminationForm] = useState({
    terminationDate: now.toISOString().split('T')[0],
    terminationType: 'WITHOUT_CAUSE',
    noticeType: 'INDEMNIFIED',
    notes: '',
  });
  const [terminationPreview, setTerminationPreview] = useState(null);

  // Mês/Ano selecionado para Estimativa de Jornada
  const [estimateYear, setEstimateYear] = useState(currentYear);
  const [estimateMonth, setEstimateMonth] = useState(currentMonth);

  // Buscar funcionários ativos
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users-for-salary'],
    queryFn: () => userService.getUsers({ limit: 100, active: true }),
    staleTime: 5 * 60 * 1000,
  });

  const users = useMemo(() => {
    if (!usersData) return [];
    // Backend: { success, data: { users, pagination } }
    if (Array.isArray(usersData?.data?.users)) return usersData.data.users;
    if (Array.isArray(usersData?.data?.data?.users)) return usersData.data.data.users;
    if (Array.isArray(usersData?.data)) return usersData.data;
    return [];
  }, [usersData]);

  // Buscar jornadas de trabalho do funcionário (para estimar horas)
  const { data: schedulesData } = useQuery({
    queryKey: ['work-schedules-salary', selectedUserId],
    queryFn: () =>
      selectedUserId ? workScheduleService.getWorkSchedulesByUser(selectedUserId, { is_active: true }) : Promise.resolve({ data: [] }),
    enabled: !!selectedUserId,
    staleTime: 5 * 60 * 1000,
  });

  const schedules = useMemo(() => {
    if (!schedulesData) return [];
    const raw = schedulesData.data;
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === 'object' && Array.isArray(raw.data)) return raw.data;
    return [];
  }, [schedulesData]);

  // Buscar configuração de salário do funcionário selecionado
  const { data: salaryConfigData, refetch: refetchSalary, isFetching: isLoadingSalary } = useQuery({
    queryKey: ['user-salary-config', selectedUserId],
    queryFn: () => (selectedUserId ? userService.getUserSalaryConfig(selectedUserId) : Promise.resolve({ data: null })),
    enabled: !!selectedUserId,
    staleTime: 0,
  });

  // DESATIVADO - Cálculo de horas extras baseado em ponto removido
  // Horas extras devem ser inseridas manualmente no holerite
  // const { data: overtimeData, refetch: refetchOvertime, isLoading: isLoadingOvertime } = useQuery({
  //   queryKey: ['user-overtime', selectedUserId, currentYear, currentMonth],
  //   queryFn: () => (selectedUserId ? userService.calculateMonthlyOvertime(selectedUserId, currentYear, currentMonth) : Promise.resolve({ data: null })),
  //   enabled: !!selectedUserId,
  //   staleTime: 0,
  // });

  // Buscar sábados trabalhados do funcionário selecionado
  const { data: workedSaturdaysData } = useQuery({
    queryKey: ['worked-saturdays', selectedUserId, estimateYear, estimateMonth],
    queryFn: () => workedSaturdaysService.getWorkedSaturdays({
      userId: selectedUserId,
      year: estimateYear,
      month: estimateMonth,
    }),
    enabled: !!selectedUserId,
    staleTime: 5 * 60 * 1000,
  });

  // Processar sábados trabalhados
  const workedSaturdays = useMemo(() => {
    if (!workedSaturdaysData?.data?.data) return [];
    return workedSaturdaysData.data.data
      .filter(s => s.worked === true)
      .map(s => s.date);
  }, [workedSaturdaysData]);

  // Buscar tabela INSS ativa para cálculo progressivo
  const { data: inssActiveData } = useQuery({
    queryKey: ['inss-active-table'],
    queryFn: () => inssTablesService.getInssTables({ active: 'true' }),
    staleTime: 5 * 60 * 1000,
  });

  const calculatedOvertime = useMemo(() => {
    // Sempre retornar 0 - horas extras devem ser inseridas manualmente
    return {
      overtime_hours_weekday: 0,
      overtime_hours_weekend: 0,
    };
  }, []);

  useEffect(() => {
    if (!selectedUserId) return;
    const cfg = salaryConfigData?.data?.data || null;
    if (cfg) {
      setFormData({
        fixed_saturday_amount: cfg.fixed_saturday_amount ?? '',
        overtime_rate_weekday: cfg.overtime_rate_weekday ?? 0,
        overtime_rate_weekend: cfg.overtime_rate_weekend ?? 0,
        overtime_manual_rate_weekday: cfg.overtime_manual_rate_weekday ?? '',
        overtime_manual_rate_weekend: cfg.overtime_manual_rate_weekend ?? '',
        salary_modality: cfg.salary_modality || 'MONTHLY',
        monthly_base_salary: cfg.monthly_base_salary ?? '',
        inss_percentage: cfg.inss_percentage ?? 7.5,
        has_work_card: cfg.has_work_card !== undefined ? cfg.has_work_card : true,
        apply_inss: cfg.apply_inss !== undefined ? cfg.apply_inss : true,
        apply_fgts: cfg.apply_fgts !== undefined ? cfg.apply_fgts : true,
        fgts_percentage: cfg.fgts_percentage ?? 8.0,
        net_salary_override: cfg.net_salary_override ?? '',
        use_real_workload: cfg.use_real_workload ?? false,
        deduct_lunch_break: cfg.deduct_lunch_break ?? true,
        calculated_hours_per_month: cfg.calculated_hours_per_month ?? 220,
      });
    } else {
      setFormData({
        fixed_saturday_amount: '',
        overtime_rate_weekday: 0,
        overtime_rate_weekend: 0,
        overtime_manual_rate_weekday: '',
        overtime_manual_rate_weekend: '',
        salary_modality: 'MONTHLY',
        monthly_base_salary: '',
        inss_percentage: 7.5,
        has_work_card: true,
        apply_inss: true,
        apply_fgts: true,
        fgts_percentage: 8.0,
        net_salary_override: '',
        use_real_workload: false,
        deduct_lunch_break: true,
        calculated_hours_per_month: 220,
      });
    }
  }, [selectedUserId, salaryConfigData]);

  const upsertMutation = useMutation({
    mutationFn: ({ userId, data }) => userService.upsertUserSalaryConfig(userId, data),
    onSuccess: (response) => {
      toast.success('Configuração de salário salva com sucesso!');
      if (response.data?.warning?.type === 'OVERRIDE_OUT_OF_RANGE') {
        toast.warning(response.data.warning.message, { duration: 8000 });
      }
      queryClient.invalidateQueries(['user-salary-config', selectedUserId]);
      refetchSalary();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao salvar configuração de salário');
    },
  });

  // Buscar resumo do 13º Salário
  const {
    data: thirteenthData,
    isLoading: isLoading13th,
    refetch: refetch13th
  } = useQuery({
    queryKey: ['thirteenth-summary', thirteenthYear],
    queryFn: () => payslipService.getThirteenthSummary(thirteenthYear),
    enabled: activeView === '13TH',
  });

  // Mutações de 13º Salário
  const generate13thMutation = useMutation({
    mutationFn: ({ userId, parcel }) => payslipService.generateThirteenth({ userId, year: thirteenthYear, parcel }),
    onSuccess: (res) => {
      toast.success(res?.data?.message || 'Holerite de 13º gerado com sucesso!');
      refetch13th();
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Erro ao gerar 13º.')
  });

  const generate13thBulkMutation = useMutation({
    mutationFn: (parcel) => payslipService.generateThirteenthBulk({ year: thirteenthYear, parcel }),
    onSuccess: (res) => {
      toast.success(res?.data?.message || 'Geração em lote de 13º concluída!');
      refetch13th();
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Erro ao gerar 13º em lote.')
  });

  // Mutações de Rescisão Contratual
  const previewTerminationMutation = useMutation({
    mutationFn: () => payslipService.previewTermination({ userId: selectedUserId, ...terminationForm }),
    onSuccess: (res) => {
      setTerminationPreview(res?.data?.data);
      toast.success('Cálculo rescisório atualizado com sucesso!');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Erro ao simular rescisão.')
  });

  const confirmTerminationMutation = useMutation({
    mutationFn: () => payslipService.confirmTermination({ userId: selectedUserId, ...terminationForm }),
    onSuccess: (res) => {
      toast.success('Rescisão homologada e colaborador inativado com sucesso!');
      setShowTerminationModal(false);
      setTerminationPreview(null);
      queryClient.invalidateQueries(['users-for-salary']);
      setSelectedUserId('');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Erro ao homologar rescisão.')
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Cálculo de horas previstas com base nas jornadas cadastradas
  const hoursPreview = useMemo(() => {
    if (!schedules || schedules.length === 0) {
      return {
        weekdayHoursMonth: 0,
        weekendHoursMonth: 0,
        saturdaysCount: 0,
      };
    }

    const year = estimateYear;
    const month = estimateMonth - 1; // 0-11
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    let weekdayHoursMonth = 0;
    let weekendHoursMonth = 0; // Apenas domingos
    let saturdaysCount = 0;

    // Para cada dia do mês, somar as horas das jornadas ativas daquele dia da semana
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      const dow = currentDate.getDay(); // 0 = domingo, 6 = sábado

      const schedulesForDay = schedules.filter(
        (s) => s.is_active && s.day_of_week === dow
      );

      if (schedulesForDay.length === 0) continue;

      // IMPORTANTE: Contar sábados apenas se:
      // 1. For sábado (dow === 6)
      // 2. Houver jornada cadastrada para sábado
      // 3. O sábado estiver marcado como trabalhado na tabela worked_saturdays
      if (dow === 6 && schedulesForDay.length > 0) {
        // Se não há usuário selecionado ou dados de sábados trabalhados, assume que SIM trabalhou
        if (!selectedUserId || workedSaturdays.length === 0) {
          saturdaysCount++;
        } else {
          // Verificar se este sábado está na lista de trabalhados
          const dateStr = currentDate.toISOString().split('T')[0];
          const isWorked = workedSaturdays.some(s => s.startsWith(dateStr));
          if (isWorked) {
            saturdaysCount++;
          }
        }
      }

      schedulesForDay.forEach((s) => {
        const start = s.start_time;
        const end = s.end_time;
        if (!start || !end) return;

        const [sh, sm] = start.split(':').map(Number);
        const [eh, em] = end.split(':').map(Number);
        // Calcular minutos totais do período (entrada até saída)
        let minutes = (eh * 60 + em) - (sh * 60 + sm);

        // IMPORTANTE: Descontar horário de almoço do cálculo do salário
        // O horário de almoço cadastrado na jornada será abatido da base de cálculo
        if (formData.deduct_lunch_break && s.break_start_time && s.break_end_time) {
          const [bsh, bsm] = s.break_start_time.split(':').map(Number);
          const [beh, bem] = s.break_end_time.split(':').map(Number);
          minutes -= (beh * 60 + bem) - (bsh * 60 + bsm); // Descontar intervalo de almoço
        }

        const hours = minutes / 60;
        const isSunday = dow === 0;

        if (isSunday) {
          weekendHoursMonth += hours; // Apenas domingos
        } else if (dow !== 6) {
          // Não contar sábados aqui (são valor fixo)
          weekdayHoursMonth += hours;
        }
      });
    }

    return {
      weekdayHoursMonth,
      weekendHoursMonth,
      saturdaysCount,
      daysInMonth,
    };
  }, [estimateYear, estimateMonth, schedules, workedSaturdays, formData.deduct_lunch_break]);

// Cálculo do valor para mensalistas
  // Valor do dia = salário / 30
  // Valor da hora = salário / 220 (fator padrão CLT)
  const hourlyRateInfo = useMemo(() => {
    const monthlySalary = Number(formData.monthly_base_salary || 0);
    if (!monthlySalary || monthlySalary <= 0) {
      return { hourlyRate: 0, dailyRate: 0, hoursPerDay: 8, divisor: 220, daysInMonth: 30 };
    }

    const daysInMonth = 30; // Fator fixo para valor do dia
    const dailyRate = monthlySalary / daysInMonth;
    const hourlyRate = monthlySalary / 220; // Fórmula: salário / 220 (padrão CLT)

    return {
      hourlyRate: Math.round(hourlyRate * 100) / 100,
      dailyRate: Math.round(dailyRate * 100) / 100,
      hoursPerDay: 8,
      divisor: 220,
      daysInMonth,
    };
  }, [formData.monthly_base_salary, hoursPreview]);

  // Cálculo de valor de horas extras baseado nas horas calculadas + taxa horária do salário mensal
  const overtimeCalculation = useMemo(() => {
    const monthlyBase = Number(formData.monthly_base_salary || 0);

    // Contar dias úteis no mês
    const daysInMonth = hoursPreview.daysInMonth || 30;
    let weekdaysInMonth = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(estimateYear, estimateMonth - 1, day);
      const dow = currentDate.getDay();
      if (dow === 0 || dow === 6) continue;
      const hasSchedule = schedules && schedules.some(s => s.is_active && s.day_of_week === dow);
      if (hasSchedule) {
        weekdaysInMonth++;
      }
    }

    // Padrão CLT: 8 horas/dia
    const STANDARD_HOURS_PER_DAY = 8;
    const divisor = weekdaysInMonth > 0 ? STANDARD_HOURS_PER_DAY * weekdaysInMonth : 220;
    const hourlyRate = monthlyBase / divisor;

    const otWeekdayHours = calculatedOvertime.overtime_hours_weekday || 0;
    const otWeekendHours = calculatedOvertime.overtime_hours_weekend || 0;

    const otRateWeekday = Number(formData.overtime_rate_weekday || 0);
    const otRateWeekend = Number(formData.overtime_rate_weekend || 0);

    // Calcular multiplicador baseado no adicional
    // 0% = 1.0x, 50% = 1.5x, 100% = 2.0x
    const multiplierWeekday = otRateWeekday === 0 ? 1.0 : otRateWeekday === 50 ? 1.5 : 2.0;
    const multiplierWeekend = otRateWeekend === 0 ? 1.0 : otRateWeekend === 50 ? 1.5 : 2.0;

    const otWeekdayValue = otWeekdayHours * hourlyRate * multiplierWeekday;
    const otWeekendValue = otWeekendHours * hourlyRate * multiplierWeekend;

    return {
      otWeekdayHours,
      otWeekendHours,
      otWeekdayValue,
      otWeekendValue,
      totalOvertimeValue: otWeekdayValue + otWeekendValue,
    };
  }, [formData, calculatedOvertime, hoursPreview, schedules, estimateYear, estimateMonth]);

// Cálculo progressivo de INSS usando tabela do banco
// Fórmula: para cada faixa, soma cumulativa = previousMax × previousPercentage + taxable × currentPercentage
const calculateInssProgressive = (salary, inssRanges) => {
  if (!inssRanges || inssRanges.length === 0) {
    const percentage = 7.5;
    return { deduction: salary * (percentage / 100), percentage };
  }

  const sortedRanges = [...inssRanges].sort((a, b) => a.max - b.max);

  for (let i = 0; i < sortedRanges.length; i++) {
    const range = sortedRanges[i];

    if (salary <= range.max) {
      let totalInss = 0;

      if (i === 0) {
        // Primeira faixa: salary × percentage
        totalInss = salary * (range.percentage / 100);
      } else {
        // Faixa seguinte: cumulative (taxa anterior sobre todo salário anterior) + taxable × current percentage
        // cumulative = previousMax × previousPercentage (não subtrai deduction - deduction já está incluso)
        const previousRange = sortedRanges[i - 1];
        const cumulative = previousRange.max * (previousRange.percentage / 100);
        const taxable = salary - previousRange.max;
        totalInss = cumulative + taxable * (range.percentage / 100);
      }

      return { deduction: Math.max(0, totalInss), percentage: range.percentage };
    }
  }

  // Acima do último range
  const lastRange = sortedRanges[sortedRanges.length - 1];
  const cumulative = lastRange.max * (lastRange.percentage / 100);
  const taxableAboveLast = salary - lastRange.max;
  const totalInss = cumulative + taxableAboveLast * (lastRange.percentage / 100);
  return { deduction: totalInss, percentage: lastRange.percentage };
};

  const estimatedMonthlySalary = useMemo(() => {
    const monthlyBase = Number(formData.monthly_base_salary || 0);
    const fixedSaturday = Number(formData.fixed_saturday_amount || 0);
    const hasWorkCard = formData.has_work_card !== false;
    const applyInss = formData.apply_inss !== false;
    const applyFgts = formData.apply_fgts !== false;
    const fgtsPercentage = Number(formData.fgts_percentage || 8);
    const netSalaryOverride = formData.net_salary_override !== '' ? Number(formData.net_salary_override) : null;

    // Valor fixo de sábados
    const saturdayTotal = hoursPreview.saturdaysCount * fixedSaturday;

    // Proventos = Salário base + sábado + horas extras
    const earnings = monthlyBase + saturdayTotal + overtimeCalculation.totalOvertimeValue;

    let inssDeduction = 0;
    let inssPercentage = 0;
    let fgtsDeduction = 0;
    let netTotal = 0;

    if (hasWorkCard) {
      // Funcionário com carteira: cálculo de encargos sobre o salário base
      const baseSalaryForInss = monthlyBase;

      // Obter faixas do INSS do banco
      const inssTable = inssActiveData?.data?.data?.[0];
      const inssRanges = inssTable?.ranges || [];

      if (applyInss) {
        const inssResult = calculateInssProgressive(baseSalaryForInss, inssRanges);
        inssDeduction = inssResult.deduction;
        inssPercentage = inssResult.percentage;
      }

      if (applyFgts) {
        fgtsDeduction = baseSalaryForInss * (fgtsPercentage / 100);
      }

      // FGTS é encargo da empresa (depósito em conta vinculada) e NÃO é descontado do salário líquido do colaborador
      netTotal = earnings - inssDeduction;
    } else {
      // Funcionário sem carteira: valor registrado é o valor líquido
      netTotal = netSalaryOverride !== null ? netSalaryOverride : earnings;
    }

    return {
      earnings,
      inssDeduction,
      inssPercentage,
      fgtsDeduction,
      netTotal: Number.isNaN(netTotal) ? 0 : netTotal,
      fgtsPercentage,
      hasWorkCard,
      applyInss,
      applyFgts,
      inssRanges: inssActiveData?.data?.data?.[0]?.ranges || [],
    };
  }, [formData, hoursPreview, overtimeCalculation, inssActiveData]);

  const selectedUser = users.find((u) => String(u.id) === String(selectedUserId));

  return (
    <div className="p-6 space-y-6">
      {/* Header com Navegação em Abas */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestão de Remuneração & RH</h1>
          <p className="text-gray-600">
            {activeView === 'SALARY'
              ? 'Configure o salário base mensal, encargos e parâmetros contratuais.'
              : 'Gestão, cálculo de avos e geração da 1ª e 2ª parcelas do 13º Salário (Lei 4.090/62).'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-100 p-1 rounded-xl border border-gray-200">
            <button
              type="button"
              onClick={() => setActiveView('SALARY')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${
                activeView === 'SALARY'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Briefcase className="w-4 h-4" />
              Salários & Jornadas
            </button>
            <button
              type="button"
              onClick={() => setActiveView('13TH')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${
                activeView === '13TH'
                  ? 'bg-white text-amber-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Gift className="w-4 h-4 text-amber-600" />
              13º Salário
            </button>
          </div>
          <a
            href="/users/faixas-inss"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2"
          >
            <Calculator className="w-4 h-4" />
            Faixas INSS
          </a>
        </div>
      </div>

      {activeView === 'SALARY' && (
        <>
          {/* Seleção de Funcionário */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-500" />
                  Funcionário
                </label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isLoadingUsers}
                >
                  <option value="">
                    {isLoadingUsers ? 'Carregando funcionários...' : 'Selecione um funcionário'}
                  </option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name} ({u.role})
                    </option>
                  ))}
                </select>
              </div>
              {selectedUser && (
                <div className="sm:self-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowTerminationModal(true);
                      setTerminationPreview(null);
                    }}
                    className="px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 flex items-center gap-2 text-sm font-medium transition-colors"
                  >
                    <UserMinus className="w-4 h-4 text-red-600" />
                    Simular / Homologar Rescisão
                  </button>
                </div>
              )}
            </div>
          </div>

      {selectedUser && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Formulário de valores */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-[#49943d]" />
              Configuração de Salário
            </h2>

            {isLoadingSalary && (
              <p className="text-sm text-gray-500 mb-2">Carregando configuração atual...</p>
            )}

            {/* Salário Mensal Base - Sempre visível (modo mensalista) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Salário mensal base (R$) *
                </label>
                <input
                    type="number"
                    name="monthly_base_salary"
                    value={formData.monthly_base_salary}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    step="0.01"
                    placeholder="Ex: 3500.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Usado para calcular o valor da hora do mensalista: salário / divisor. Base para horas extras e faltas.
                  </p>
              </div>
            </div>

            {/* Cálculo da Hora */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Calculator className="w-4 h-4 text-blue-600" />
                Cálculo da Hora
              </h3>

              <div className="space-y-3">
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="use_real_workload"
                      checked={!formData.use_real_workload}
                      onChange={() => setFormData(prev => ({ ...prev, use_real_workload: false }))}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">Usar divisor fixo (220h)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="use_real_workload"
                      checked={formData.use_real_workload}
                      onChange={() => setFormData(prev => ({ ...prev, use_real_workload: true }))}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">Usar jornada real do funcionário</span>
                  </label>
                </div>

                {formData.use_real_workload && (
                  <div className="pl-6 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.deduct_lunch_break}
                        onChange={(e) => setFormData(prev => ({ ...prev, deduct_lunch_break: e.target.checked }))}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Descontar intervalo de almoço</span>
                    </label>

                    <div className="mt-3 p-4 bg-white rounded border border-blue-200 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-700">Mês de referência:</p>
                        <span className="text-sm font-bold text-blue-700">
                          {new Date(estimateYear, estimateMonth - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                        </span>
                      </div>

                      <div className="border-t border-gray-200 pt-3">
                        <p className="text-xs text-gray-600 mb-2">Horas estimadas no mês (baseadas nas jornadas):</p>
                        <div className="flex items-center gap-3">
                          <div className="flex flex-col">
                            <span className="text-2xl font-bold text-blue-700">{hoursPreview.weekdayHoursMonth.toFixed(1)}h</span>
                            <span className="text-xs text-gray-500">dias úteis</span>
                          </div>
                          {hoursPreview.weekendHoursMonth > 0 && (
                            <div className="flex flex-col border-l border-gray-300 pl-3">
                              <span className="text-lg font-bold text-gray-600">+ {hoursPreview.weekendHoursMonth.toFixed(1)}h</span>
                              <span className="text-xs text-gray-500">domingos</span>
                            </div>
                          )}
                          {hoursPreview.saturdaysCount > 0 && (
                            <div className="flex flex-col border-l border-gray-300 pl-3">
                              <span className="text-lg font-bold text-gray-600">+ {hoursPreview.saturdaysCount}</span>
                              <span className="text-xs text-gray-500">sábados</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {formData.monthly_base_salary && hourlyRateInfo.hourlyRate > 0 && (
                        <div className="border-t border-gray-200 pt-3 space-y-2">
                          <p className="text-xs text-gray-600 font-medium">Cálculo do valor da hora (padrão CLT):</p>
                          <div className="bg-gray-50 p-3 rounded text-xs space-y-1">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Salário mensal:</span>
                              <span className="font-medium">R$ {Number(formData.monthly_base_salary).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">÷ 220 (fator CLT):</span>
                              <span className="font-medium">R$ {hourlyRateInfo.hourlyRate.toFixed(2)}/h</span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-400 text-center">
                            Fórmula: R$ {Number(formData.monthly_base_salary).toLocaleString('pt-BR')} ÷ 220 = R$ {hourlyRateInfo.hourlyRate.toFixed(2)}/h
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!formData.use_real_workload && (
                  <p className="text-xs text-gray-500 pl-6">
                    Divisor padrão CLT: 220 horas/mês (44h semanais × 5 semanas)
                  </p>
                )}
              </div>
            </div>

            {/* Carteira de Trabalho e Encargos */}
            <div className={`mt-6 p-4 rounded-lg border ${formData.has_work_card ? 'bg-purple-50 border-purple-200' : 'bg-orange-50 border-orange-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2">
                  <Briefcase className={`w-5 h-5 ${formData.has_work_card ? 'text-purple-600' : 'text-orange-600'}`} />
                  Carteira de Trabalho
                </h3>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${formData.has_work_card ? 'text-purple-700' : 'text-orange-700'}`}>
                    {formData.has_work_card ? 'Com Carteira (CLT)' : 'Sem Carteira'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, has_work_card: !prev.has_work_card }))}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${formData.has_work_card ? 'bg-purple-600' : 'bg-orange-400'}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.has_work_card ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                  </button>
                </div>
              </div>

              <p className={`text-sm mb-4 ${formData.has_work_card ? 'text-purple-700' : 'text-orange-700'}`}>
                {formData.has_work_card
                  ? 'Funcionário com carteira assinada. Configure os encargos obrigatórios (INSS e FGTS).'
                  : 'Funcionário sem carteira. O valor registrado será o valor líquido que o funcionário recebe.'}
              </p>

              {/* Configuração de Encargos - apenas para quem tem carteira */}
              {formData.has_work_card && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Desconto INSS */}
                  <div className="p-3 bg-white rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <Shield className="w-4 h-4 text-blue-600" />
                        Descontar INSS
                      </label>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, apply_inss: !prev.apply_inss }))}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${formData.apply_inss ? 'bg-blue-600' : 'bg-gray-300'}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.apply_inss ? 'translate-x-4' : 'translate-x-0'}`}
                        />
                      </button>
                    </div>
                    {formData.apply_inss && (
                      <p className="text-sm text-purple-700 font-medium">
                        Cálculo progressivo: {estimatedMonthlySalary.inssPercentage}% (marginal)
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.apply_inss ? 'Usa tabela progressiva do banco de dados.' : 'INSS não será descontado.'}
                    </p>
                  </div>

                  {/* Desconto FGTS */}
                  <div className="p-3 bg-white rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <Shield className="w-4 h-4 text-green-600" />
                        Descontar FGTS
                      </label>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, apply_fgts: !prev.apply_fgts }))}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${formData.apply_fgts ? 'bg-green-600' : 'bg-gray-300'}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.apply_fgts ? 'translate-x-4' : 'translate-x-0'}`}
                        />
                      </button>
                    </div>
                    {formData.apply_fgts && (
                      <input
                        type="number"
                        name="fgts_percentage"
                        value={formData.fgts_percentage}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="Ex: 8.0"
                      />
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.apply_fgts ? `Percentual do FGTS (padrão: ${formData.fgts_percentage}%).` : 'FGTS não será calculado.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Tabela de Faixas de INSS - apenas para quem tem carteira */}
              {formData.has_work_card && formData.apply_inss && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-2">
                    <Calculator className="w-4 h-4" />
                    Tabela de Descontos INSS - Cálculo Progressivo
                  </h4>
                  {estimatedMonthlySalary.inssRanges && estimatedMonthlySalary.inssRanges.length > 0 ? (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-blue-100">
                          <th className="px-2 py-1 text-left text-blue-800">Faixa Salarial</th>
                          <th className="px-2 py-1 text-right text-blue-800">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {estimatedMonthlySalary.inssRanges.map((range, idx) => (
                          <tr key={idx} className="border-b border-blue-100">
                            <td className="px-2 py-1">
                              {idx === 0
                                ? `Até R$ ${Number(range.max).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                : `Acima de R$ ${Number(estimatedMonthlySalary.inssRanges[idx - 1]?.max || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                              }
                            </td>
                            <td className="px-2 py-1 text-right">{range.percentage}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-blue-100">
                          <th className="px-2 py-1 text-left text-blue-800">Faixa Salarial</th>
                          <th className="px-2 py-1 text-right text-blue-800">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-blue-100">
                          <td className="px-2 py-1">Até R$ 1.518,00</td>
                          <td className="px-2 py-1 text-right">7,5%</td>
                        </tr>
                        <tr className="border-b border-blue-100">
                          <td className="px-2 py-1">De R$ 1.518,01 até R$ 2.628,00</td>
                          <td className="px-2 py-1 text-right">9%</td>
                        </tr>
                        <tr className="border-b border-blue-100">
                          <td className="px-2 py-1">De R$ 2.628,01 até R$ 3.921,00</td>
                          <td className="px-2 py-1 text-right">12%</td>
                        </tr>
                        <tr>
                          <td className="px-2 py-1">De R$ 3.921,01 até R$ 7.786,02</td>
                          <td className="px-2 py-1 text-right">14%</td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                  <p className="text-xs text-blue-600 mt-2">
                    Cálculo progressivo usando tabela ativa do sistema.
                    <a href="/users/faixas-inss" className="ml-1 underline hover:no-underline">Editar faixas</a>
                  </p>
                </div>
              )}

              {/* Override de Salário Líquido - apenas para quem NÃO tem carteira */}
              {!formData.has_work_card && (
                <div className="p-3 bg-white rounded-lg border border-orange-200">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Calculator className="w-4 h-4 text-orange-600" />
                    Salário Líquido Registrado (R$)
                  </label>
                  <input
                    type="number"
                    name="net_salary_override"
                    value={formData.net_salary_override}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    min="0"
                    step="0.01"
                    placeholder="Ex: 3500.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Este será o valor líquido registrado para o funcionário. Os cálculos de proventos são apenas para referência.
                  </p>
                </div>
              )}
            </div>

            {/* Valor fixo sábado - aplicável para mensalistas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor fixo - Sábado (R$)
                </label>
                <input
                  type="number"
                  name="fixed_saturday_amount"
                  value={formData.fixed_saturday_amount}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  step="0.01"
                  placeholder="Ex: 150.00"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Valor fixo combinado com o colaborador para cada sábado trabalhado.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Desconto INSS (%)
                </label>
                <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                  Automático por faixa salarial
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Percentual calculado automaticamente conforme tabela de faixas.
                </p>
              </div>
            </div>

            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Nota:</strong> Os campos de <strong>Adicional Mensal</strong> e <strong>Adicional Feriado</strong> devem ser inseridos diretamente no holerite durante a aprovação pelo gerente.
              </p>
            </div>

            {/* Seção de Horas Extras */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-md font-semibold text-gray-900">
                  Modalidade de Pagamento de Horas Extras
                </h3>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>Nota:</strong> O controle de ponto foi desativado. As horas extras devem ser inseridas manualmente pelo gerente durante a aprovação do holerite.
                  As modalidades abaixo definem como as horas extras serão calculadas quando inseridas.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Horas Extras - Dias Úteis */}
                <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700">Dias Úteis</h4>

                  {/* Toggle Modo Manual */}
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!formData.overtime_manual_rate_weekday}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData(prev => ({
                            ...prev,
                            overtime_manual_rate_weekday: checked ? String(prev.overtime_manual_rate_weekday || hourlyRateInfo.hourlyRate.toFixed(2)) : ''
                          }));
                        }}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Modo Manual (valor fixo)</span>
                    </label>
                  </div>

                  {formData.overtime_manual_rate_weekday !== '' && formData.overtime_manual_rate_weekday !== null ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Valor da hora extra (R$) - Dias Úteis
                      </label>
                      <input
                        type="number"
                        name="overtime_manual_rate_weekday"
                        value={formData.overtime_manual_rate_weekday}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="0"
                        step="0.01"
                        placeholder="Ex: 15.00"
                      />
                      <p className="text-xs text-green-600 mt-1">
                        ✓ Este valor será pago diretamente por hora extra
                      </p>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Modalidade de pagamento
                      </label>
                      <select
                        name="overtime_rate_weekday"
                        value={formData.overtime_rate_weekday}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value={0}>0% (valor normal da hora)</option>
                        <option value={50}>50% (1.5x o valor da hora)</option>
                        <option value={100}>100% (2x o valor da hora)</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Horas Extras - Finais de Semana */}
                <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700">Finais de Semana</h4>

                  {/* Toggle Modo Manual */}
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!formData.overtime_manual_rate_weekend}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData(prev => ({
                            ...prev,
                            overtime_manual_rate_weekend: checked ? String(prev.overtime_manual_rate_weekend || hourlyRateInfo.hourlyRate.toFixed(2)) : ''
                          }));
                        }}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Modo Manual (valor fixo)</span>
                    </label>
                  </div>

                  {formData.overtime_manual_rate_weekend !== '' && formData.overtime_manual_rate_weekend !== null ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Valor da hora extra (R$) - Finais de Semana
                      </label>
                      <input
                        type="number"
                        name="overtime_manual_rate_weekend"
                        value={formData.overtime_manual_rate_weekend}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="0"
                        step="0.01"
                        placeholder="Ex: 20.00"
                      />
                      <p className="text-xs text-green-600 mt-1">
                        ✓ Este valor será pago diretamente por hora extra
                      </p>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Modalidade de pagamento
                      </label>
                      <select
                        name="overtime_rate_weekend"
                        value={formData.overtime_rate_weekend}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value={0}>0% (valor normal da hora)</option>
                        <option value={50}>50% (1.5x o valor da hora)</option>
                        <option value={100}>100% (2x o valor da hora)</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-200 mt-4">
              <button
                onClick={() =>
                  upsertMutation.mutate({
                    userId: selectedUserId,
                    data: formData,
                  })
                }
                disabled={upsertMutation.isLoading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630] disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {upsertMutation.isLoading ? 'Salvando...' : 'Salvar Configuração'}
              </button>
            </div>
          </div>

          {/* Estimativa baseada em jornadas */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              Estimativa de Jornada
            </h2>
            {schedules.length === 0 ? (
              <p className="text-sm text-gray-500">
                Nenhuma jornada de trabalho cadastrada para este funcionário.
                Cadastre as jornadas para ter uma estimativa de horas semanais e mensais.
              </p>
            ) : (
              <div className="space-y-2 text-sm text-gray-700">
                <div className="p-2 bg-blue-50 rounded border border-blue-200">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-blue-600 font-medium">Mês de referência</p>
                    <div className="flex gap-2">
                      <select
                        value={estimateMonth}
                        onChange={(e) => setEstimateMonth(Number(e.target.value))}
                        className="text-xs border border-blue-300 rounded px-1 py-0.5"
                      >
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                          <option key={m} value={m}>
                            {new Date(2000, m - 1, 1).toLocaleDateString('pt-BR', { month: 'short' })}
                          </option>
                        ))}
                      </select>
                      <select
                        value={estimateYear}
                        onChange={(e) => setEstimateYear(Number(e.target.value))}
                        className="text-xs border border-blue-300 rounded px-1 py-0.5"
                      >
                        {[currentYear - 1, currentYear, currentYear + 1].map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => queryClient.invalidateQueries(['worked-saturdays'])}
                        className="text-xs bg-blue-500 text-white rounded px-2 py-0.5 hover:bg-blue-600"
                      >
                        Atualizar
                      </button>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-blue-900 mt-1">
                    {new Date(estimateYear, estimateMonth - 1, 1).toLocaleDateString('pt-BR', {
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
                <p className="flex items-center gap-2 text-xs text-gray-500">
                  <Info className="w-3 h-3" />
                  Cálculo exato para o mês selecionado, com base nas jornadas cadastradas e nos dias do calendário.
                </p>
                <div className="mt-2">
                  <p className="font-medium text-gray-900 mb-1">Horas previstas no mês</p>
                  <p>Dias úteis: {hoursPreview.weekdayHoursMonth.toFixed(2)} h</p>
                  <p>Domingos: {hoursPreview.weekendHoursMonth.toFixed(2)} h</p>
                  <p className="mt-2">
                    Sábados: {hoursPreview.saturdaysCount} dia(s) - Valor fixo: {Number(formData.fixed_saturday_amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} cada
                  </p>
                </div>
                <div className="mt-3 border-t border-gray-200 pt-3">
                  <div className="flex items-center gap-2 mb-2">
                    {estimatedMonthlySalary.hasWorkCard ? (
                      <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                        CLT - Com Encargos
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">
                        Sem Carteira
                      </span>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 mb-1">
                    {estimatedMonthlySalary.hasWorkCard ? 'Estimativa salarial com encargos' : 'Valor registrado para referência'}
                  </p>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span>Total Proventos:</span>
                      <span className="font-medium">{estimatedMonthlySalary.earnings.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                    </div>

                    {estimatedMonthlySalary.hasWorkCard ? (
                      <>
                        {estimatedMonthlySalary.inssDeduction > 0 && (
                          <div className="flex justify-between text-red-600">
                            <span>Desconto INSS ({estimatedMonthlySalary.inssPercentage}%):</span>
                            <span>- {estimatedMonthlySalary.inssDeduction.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                          </div>
                        )}
                        {estimatedMonthlySalary.fgtsDeduction > 0 && (
                          <div className="flex justify-between text-blue-700 bg-blue-50/70 px-2 py-1 rounded">
                            <span>FGTS ({estimatedMonthlySalary.fgtsPercentage}% - Encargo Empresa / Informativo):</span>
                            <span className="font-semibold">{estimatedMonthlySalary.fgtsDeduction.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                          </div>
                        )}
                        {!estimatedMonthlySalary.applyInss && (
                          <div className="flex justify-between text-gray-500">
                            <span>INSS:</span>
                            <span>Isento</span>
                          </div>
                        )}
                        {!estimatedMonthlySalary.applyFgts && (
                          <div className="flex justify-between text-gray-500">
                            <span>FGTS:</span>
                            <span>Não aplicável</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex justify-between text-blue-600">
                        <span>Valor Registrado:</span>
                        <span className="font-medium">
                          {Number(formData.net_salary_override || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between pt-2 border-t border-gray-100 mt-1">
                      <span className="font-bold">
                        {estimatedMonthlySalary.hasWorkCard ? 'Salário Líquido:' : 'Valor a Receber:'}
                      </span>
                      <span className="text-base font-bold text-[#49943d]">
                        {estimatedMonthlySalary.netTotal.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        })}
                      </span>
                    </div>
                  </div>
                  {overtimeCalculation.totalOvertimeValue > 0 && (
                    <div className="mt-2 text-xs text-gray-500">
                      <p>* Inclui {overtimeCalculation.totalOvertimeValue.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })} em horas extras estimadas</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </>
      )}

      {/* Seção de 13º Salário */}
      {activeView === '13TH' && (
        <div className="space-y-6">
          {/* Barra de Ações e Filtros de 13º */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-50 rounded-xl text-amber-600">
                <Gift className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">13º Salário - Folha Anual</h2>
                <p className="text-xs text-gray-500">Cálculo proporcional de avos e geração de holerites para quitação</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Ano:</span>
                <input
                  type="number"
                  value={thirteenthYear}
                  onChange={(e) => setThirteenthYear(parseInt(e.target.value, 10))}
                  className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-bold text-gray-900"
                />
              </div>

              <button
                type="button"
                onClick={() => generate13thBulkMutation.mutate(0)}
                disabled={generate13thBulkMutation.isPending}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-semibold flex items-center gap-2 disabled:opacity-50 transition-colors shadow-xs"
                title="Gera o 13º Salário Integral em cota única oficial de Dezembro para todos"
              >
                <Save className={`w-4 h-4 ${generate13thBulkMutation.isPending ? 'animate-spin' : ''}`} />
                Gerar Cota Única em Lote (Dezembro)
              </button>

              <button
                type="button"
                onClick={() => generate13thBulkMutation.mutate(1)}
                disabled={generate13thBulkMutation.isPending}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-semibold flex items-center gap-2 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${generate13thBulkMutation.isPending ? 'animate-spin' : ''}`} />
                Gerar 1ª Parcela em Lote (Novembro)
              </button>

              <button
                type="button"
                onClick={() => generate13thBulkMutation.mutate(2)}
                disabled={generate13thBulkMutation.isPending}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-semibold flex items-center gap-2 disabled:opacity-50 transition-colors"
              >
                <Save className={`w-4 h-4 ${generate13thBulkMutation.isPending ? 'animate-spin' : ''}`} />
                Gerar 2ª Parcela em Lote (Dezembro)
              </button>
            </div>
          </div>

          {/* Banner Informativo CLT */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800 space-y-1">
              <p className="font-semibold">Regras Oficiais de 13º Salário (Lei 4.090/62 e Lei 4.749/65):</p>
              <p>• <strong>1ª Parcela (Adiantamento):</strong> Paga até 30 de novembro, correspondente a 50% do salário proporcional aos avos do ano. Totalmente isenta de desconto de INSS e IRRF.</p>
              <p>• <strong>2ª Parcela (Quitação):</strong> Paga até 20 de dezembro. Deduz a 1ª parcela e aplica o INSS progressivo e IRRF com tributação exclusiva na fonte.</p>
            </div>
          </div>

          {/* Tabela de Colaboradores e 13º */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Quadro de Colaboradores ({thirteenthData?.data?.data?.length || 0})</span>
              <button
                type="button"
                onClick={() => refetch13th()}
                className="p-1.5 hover:bg-gray-200 rounded-lg text-gray-600 text-xs flex items-center gap-1 font-medium transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isLoading13th ? 'animate-spin' : ''}`} />
                Atualizar
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3">Colaborador</th>
                    <th className="px-4 py-3">Admissão</th>
                    <th className="px-4 py-3">Salário Base</th>
                    <th className="px-4 py-3">Avos</th>
                    <th className="px-4 py-3">Base Integral</th>
                    <th className="px-4 py-3">Cota Única (Dez)</th>
                    <th className="px-4 py-3">1ª Parcela (50%)</th>
                    <th className="px-4 py-3">2ª Parcela (Saldo)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {isLoading13th ? (
                    <tr>
                      <td colSpan="7" className="p-8 text-center text-gray-500">Carregando quadro de 13º...</td>
                    </tr>
                  ) : !thirteenthData?.data?.data?.length ? (
                    <tr>
                      <td colSpan="7" className="p-8 text-center text-gray-500">Nenhum colaborador encontrado para o ano {thirteenthYear}.</td>
                    </tr>
                  ) : (
                    thirteenthData.data.data.map((item) => (
                      <tr key={item.userId} className="hover:bg-gray-50/80 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          <div>{item.userName}</div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-xs text-gray-500">{item.role}</span>
                            <span className={`px-1.5 py-0.2 rounded text-[10px] font-semibold ${item.hasWorkCard ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                              {item.hasWorkCard ? 'CLT' : 'Sem Carteira'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs">
                          {item.hireDate ? (() => {
                            const dateStr = typeof item.hireDate === 'string' ? item.hireDate.split('T')[0] : '';
                            if (dateStr && dateStr.includes('-')) {
                              const [y, m, d] = dateStr.split('-');
                              return `${d}/${m}/${y}`;
                            }
                            return new Date(item.hireDate).toLocaleDateString('pt-BR');
                          })() : 'Não informada'}
                        </td>
                        <td className="px-4 py-3 text-gray-900 font-medium">
                          {item.monthlySalary.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full font-bold text-xs">
                            {item.avos}/12 avos
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-900 font-bold">
                          {item.baseIntegral.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </td>
                        <td className="px-4 py-3">
                          {item.singleParcel?.payslipId ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-xs font-semibold">
                              <CheckCircle2 className="w-3.5 h-3.5 text-purple-600" />
                              {Number(item.singleParcel.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-600">
                                {item.singleParcel?.estimate?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                              </span>
                              <button
                                type="button"
                                onClick={() => generate13thMutation.mutate({ userId: item.userId, parcel: 0 })}
                                disabled={generate13thMutation.isPending}
                                className="px-2 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded text-xs font-bold hover:bg-purple-100 transition-colors"
                              >
                                Gerar Integral
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {item.firstHalf.payslipId ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 border border-green-200 rounded-lg text-xs font-semibold">
                              <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                              {Number(item.firstHalf.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-600">
                                {item.firstHalf.estimate.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                              </span>
                              <button
                                type="button"
                                onClick={() => generate13thMutation.mutate({ userId: item.userId, parcel: 1 })}
                                disabled={generate13thMutation.isPending}
                                className="px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded text-xs font-bold hover:bg-amber-100 transition-colors"
                              >
                                Gerar 1ª
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {item.secondHalf.payslipId ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 border border-green-200 rounded-lg text-xs font-semibold">
                              <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                              {Number(item.secondHalf.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => generate13thMutation.mutate({ userId: item.userId, parcel: 2 })}
                              disabled={generate13thMutation.isPending}
                              className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded text-xs font-bold hover:bg-emerald-100 transition-colors"
                            >
                              Gerar 2ª Parcela
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Homologação de Rescisão Contratual */}
      {showTerminationModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 max-w-2xl w-full p-6 space-y-5 my-8">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2 text-red-600 font-bold text-lg">
                <UserMinus className="w-5 h-5" />
                Rescisão Contratual (TRCT)
              </div>
              <button
                type="button"
                onClick={() => setShowTerminationModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-800">
              <p className="font-semibold">Atenção ao Homologar:</p>
              <p>Colaborador: <strong>{selectedUser?.full_name}</strong> ({selectedUser?.role})</p>
              <p>A homologação gera o holerite de rescisão em <em>payslips</em> e inativa o colaborador no sistema.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Motivo do Desligamento *</label>
                <select
                  value={terminationForm.terminationType}
                  onChange={(e) => setTerminationForm(prev => ({ ...prev, terminationType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="WITHOUT_CAUSE">Sem Justa Causa (Empregador)</option>
                  <option value="EMPLOYEE_RESIGNATION">Pedido de Demissão (Empregado)</option>
                  <option value="WITH_CAUSE">Com Justa Causa (Empregador)</option>
                  <option value="MUTUAL_AGREEMENT">Acordo Mútuo (Art. 484-A CLT)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Tipo de Aviso Prévio *</label>
                <select
                  value={terminationForm.noticeType}
                  onChange={(e) => setTerminationForm(prev => ({ ...prev, noticeType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="INDEMNIFIED">Indenizado pela Empresa</option>
                  <option value="WORKED">Trabalhado</option>
                  <option value="DISPENSED">Dispensado</option>
                  <option value="UNWORKED_DEDUCTED">Não Cumprido (Descontar 30d)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Data do Desligamento *</label>
                <input
                  type="date"
                  value={terminationForm.terminationDate}
                  onChange={(e) => setTerminationForm(prev => ({ ...prev, terminationDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Observações Internas</label>
                <input
                  type="text"
                  placeholder="Ex: Motivo ou acordos extras"
                  value={terminationForm.notes}
                  onChange={(e) => setTerminationForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => previewTerminationMutation.mutate()}
                disabled={previewTerminationMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold flex items-center gap-2 transition-colors"
              >
                <Calculator className="w-4 h-4" />
                {previewTerminationMutation.isPending ? 'Calculando...' : 'Calcular Verbas Rescisórias'}
              </button>
            </div>

            {terminationPreview && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-bold text-gray-900 border-b border-gray-200 pb-2">
                  Espelho de Verbas Rescisórias (TRCT)
                </h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>Saldo de Salário ({terminationPreview.daysWorkedInMonth}d):</div>
                  <div className="text-right font-medium">{terminationPreview.earnings.salaryBalance.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>

                  <div>Aviso Prévio ({terminationPreview.noticeDaysTotal}d):</div>
                  <div className="text-right font-medium">{terminationPreview.earnings.noticeAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>

                  <div>13º Salário Proporcional ({terminationPreview.earnings.thirteenthAvos}/12 avos):</div>
                  <div className="text-right font-medium">{terminationPreview.earnings.thirteenthGross.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>

                  <div>Férias Vencidas ({terminationPreview.earnings.expiredVacationDays}d):</div>
                  <div className="text-right font-medium">{terminationPreview.earnings.expiredVacationAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>

                  <div>Férias Proporcionais ({terminationPreview.earnings.proportionalVacationAvos}/12 avos):</div>
                  <div className="text-right font-medium">{terminationPreview.earnings.proportionalVacationAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>

                  <div>1/3 Constitucional de Férias:</div>
                  <div className="text-right font-medium">{terminationPreview.earnings.vacationOneThirdBonus.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>

                  <div className="border-t pt-1 font-semibold text-gray-700">Total Proventos Brutos:</div>
                  <div className="border-t pt-1 text-right font-bold text-gray-900">{terminationPreview.earnings.totalEarnings.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>

                  <div className="text-red-600">Total Deduções (INSS / Adiantamentos):</div>
                  <div className="text-right font-medium text-red-600">- {terminationPreview.deductions.totalDeductions.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>

                  <div className="border-t pt-2 text-sm font-bold text-gray-900">VALOR LÍQUIDO A PAGAR:</div>
                  <div className="border-t pt-2 text-right text-base font-extrabold text-emerald-600">
                    {terminationPreview.netAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </div>
                </div>

                {terminationPreview.fgtsPenaltyPercentage > 0 && (
                  <div className="pt-2 border-t border-gray-200 text-xs text-purple-700 font-medium">
                    * Multa Rescisória de FGTS: {terminationPreview.fgtsPenaltyPercentage}% sobre o saldo vinculado para fins rescisórios (paga via GRRF).
                  </div>
                )}

                <div className="pt-3 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowTerminationModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={() => confirmTerminationMutation.mutate()}
                    disabled={confirmTerminationMutation.isPending}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-bold flex items-center gap-2 transition-colors"
                  >
                    {confirmTerminationMutation.isPending ? 'Homologando...' : 'Confirmar e Homologar Rescisão'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeSalary;
